import { useEffect, useRef, useState } from 'react';
import { FiTrash2, FiUpload } from 'react-icons/fi';

import { Button } from '@shared/ui/button';
import { Select } from '@shared/ui/select';
import type { SelectOption } from '@shared/ui/select';
import { MyModal } from '@shared/ui/my-modal';
import { PushBadge } from '@entities/marketing-push/ui';
import {
  charLength,
  engagementHint,
  formatPushNumber,
} from '@entities/marketing-push/lib';
import {
  marketingPushApi,
  asList,
  pushError,
} from '../../../api/services/marketingPush.service';
import {
  ENGAGEMENT_MODE_OPTIONS,
} from '../../../types/marketingPushInterfaces';
import type {
  EngagementMode,
  PushApp,
  PushAudienceParams,
  PushCampaignDraft,
  PushLanguage,
  PushTemplate,
} from '../../../types/marketingPushInterfaces';

const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const CARD = 'bg-white rounded-xl shadow-sm p-4 mb-4';
const selectControlStyles = { control: () => ({ height: '44px', minHeight: '44px' }) };

/* -------------------------------------------------------------------------- */
/* Приложения                                                                  */
/* -------------------------------------------------------------------------- */

export function PushTargetPicker({
  apps,
  selected,
  disabled,
  onToggle,
}: {
  apps: PushApp[];
  selected: string[];
  disabled?: boolean;
  onToggle: (code: string) => void;
}) {
  return (
    <div className={CARD}>
      <h2 className="text-base font-semibold text-[#2d2c2c] mb-3">Приложения</h2>

      {apps.map((app) => (
        <div key={app.code} className="mb-3 last:mb-0">
          <p className="text-sm font-medium text-gray-500 mb-1">{app.name}</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {app.targets.map((target) => {
              const noAudience = target.is_active && target.installations === 0;
              return (
                <label
                  key={target.code}
                  className={`flex items-start gap-2 py-1 ${
                    disabled || !target.is_active ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.includes(target.code)}
                    // Цель без интеграции выбрать нельзя: иначе повторится
                    // ситуация старого инструмента, где чекбокс был, а
                    // получателей не существовало.
                    disabled={disabled || !target.is_active}
                    onChange={() => onToggle(target.code)}
                  />
                  <span>
                    <span className="text-sm text-[#2d2c2c] inline-flex items-center gap-2">
                      {target.display_name}
                      {!target.is_active ? (
                        <PushBadge tone="neutral">нет интеграции</PushBadge>
                      ) : (
                        <PushBadge
                          tone={noAudience ? 'warning' : 'neutral'}
                          title="Активных установок"
                        >
                          {formatPushNumber(target.installations)}
                        </PushBadge>
                      )}
                    </span>
                    {noAudience && (
                      <span className="block text-xs text-amber-700">
                        Нет активных установок — получателей не будет
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}

      {selected.length === 0 && (
        <p className="mt-2 text-xs text-red-600">Выберите хотя бы одно приложение.</p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Языки                                                                       */
/* -------------------------------------------------------------------------- */

export function PushLanguageTabs({
  languages,
  selected,
  active,
  problems,
  disabled,
  onSelect,
  onAdd,
  onRemove,
}: {
  languages: PushLanguage[];
  selected: string[];
  active: string;
  /** Языки с незаполненными полями — подсвечиваются точкой. */
  problems: Set<string>;
  disabled?: boolean;
  onSelect: (code: string) => void;
  onAdd: (code: string) => void;
  onRemove: (code: string) => void;
}) {
  const available = languages.filter((language) => !selected.includes(language.code));

  return (
    <div className="mb-4 flex flex-wrap items-center gap-1 border-b border-gray-200 pb-2">
      {selected.map((code) => {
        const language = languages.find((item) => item.code === code);
        const isActive = code === active;

        return (
          <span
            key={code}
            onClick={() => onSelect(code)}
            className={`inline-flex h-9 cursor-pointer items-center gap-1 rounded-lg pl-3 pr-1 text-sm ${
              isActive ? 'bg-blue-50 text-blue-700' : 'text-[#2d2c2c] hover:bg-gray-50'
            }`}
          >
            {language?.name ?? code}
            {problems.has(code) && (
              <span
                title="Не все поля заполнены"
                aria-label="Не все поля заполнены"
                className="h-[6px] w-[6px] rounded-full bg-red-500"
              />
            )}
            {!disabled && selected.length > 1 && (
              <button
                type="button"
                aria-label={`Убрать ${language?.name ?? code}`}
                className="px-1 text-lg leading-none text-gray-400 hover:text-red-500"
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove(code);
                }}
              >
                ×
              </button>
            )}
          </span>
        );
      })}

      {!disabled && available.length > 0 && (
        <select
          value=""
          aria-label="Добавить язык"
          onChange={(event) => event.target.value && onAdd(event.target.value)}
          className="h-9 cursor-pointer rounded-lg border border-dashed border-gray-400 bg-transparent px-2 text-sm text-gray-500"
        >
          <option value="">+ язык</option>
          {available.map((language) => (
            <option key={language.code} value={language.code}>
              {language.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Изображение                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Поле изображения.
 *
 * В старом инструменте это был ввод URL руками. Оставлять так нельзя:
 * ссылка на внешний ресурс может умереть между планированием и отправкой,
 * а картинку запрашивает само приложение в момент открытия уведомления.
 * Поэтому основной путь — загрузка в наше хранилище со стабильной ссылкой,
 * ручной ввод оставлен запасным.
 */
export function PushImageField({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [manual, setManual] = useState(false);

  async function handleFile(file?: File) {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const { data } = await marketingPushApi.uploadImage(file);
      onChange(data.url);
    } catch (uploadError) {
      setError(pushError(uploadError, 'Не удалось загрузить изображение'));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="mb-4">
      <label className={FIELD_LABEL}>Изображение</label>
      <p className="mb-2 text-xs text-gray-500">
        Показывается внутри приложения, не в системном уведомлении. JPG, PNG или WebP до 2 МБ.
      </p>

      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}

      {value ? (
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-2">
          <img
            src={value}
            alt=""
            className="h-[72px] w-24 rounded bg-gray-100 object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="break-all text-xs text-gray-500">{value}</p>
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="mt-1 inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
              >
                <FiTrash2 /> Убрать
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(event) => handleFile(event.target.files?.[0])}
          />
          <Button
            isWhite
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
          >
            <span className="inline-flex items-center gap-1">
              <FiUpload /> {uploading ? 'Загрузка…' : 'Загрузить файл'}
            </span>
          </Button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setManual((flag) => !flag)}
            className="text-sm text-blue-600 hover:underline disabled:opacity-50"
          >
            {manual ? 'Скрыть ссылку' : 'Указать ссылку'}
          </button>
        </div>
      )}

      {manual && !value && (
        <div className="mt-2">
          <input
            type="url"
            placeholder="https://…"
            disabled={disabled}
            onBlur={(event) => {
              const url = event.target.value.trim();
              if (url) onChange(url);
            }}
            className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
          />
          <p className="mt-1 text-xs text-amber-700">
            Внешняя ссылка должна оставаться доступной и после отправки: приложение
            загружает картинку в момент открытия уведомления.
          </p>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Аудитория                                                                   */
/* -------------------------------------------------------------------------- */

export function PushAudienceBlock({
  audience,
  disabled,
  onChange,
  onModeChange,
}: {
  audience: PushAudienceParams;
  disabled?: boolean;
  onChange: (patch: Partial<PushAudienceParams>) => void;
  onModeChange: (mode: EngagementMode) => void;
}) {
  const mode = audience.engagement_mode;
  const options: SelectOption[] = ENGAGEMENT_MODE_OPTIONS.map((item) => ({
    value: item.value,
    label: item.label,
  }));
  const current = options.find((option) => option.value === mode) ?? null;

  return (
    <div className={CARD}>
      <h2 className="text-base font-semibold text-[#2d2c2c] mb-3">Аудитория</h2>

      <div className="w-full max-w-[420px]">
        <label className={FIELD_LABEL}>Последнее взаимодействие с приложением</label>
        <Select
          options={options}
          optionSelected={current}
          isVisible={false}
          isClearable={false}
          customStyles={selectControlStyles}
          onSelected={(option: SelectOption | null) =>
            option && onModeChange(option.value as EngagementMode)
          }
        />
        <p className="mt-1 text-xs text-gray-500">{engagementHint(mode)}</p>
      </div>

      {(mode === 'more' || mode === 'less') && (
        <div className="mt-3 w-[160px]">
          <label className={FIELD_LABEL}>Дней</label>
          <input
            type="number"
            min={1}
            max={365}
            disabled={disabled}
            value={audience.engagement_days ?? ''}
            onChange={(event) => onChange({ engagement_days: parseDays(event.target.value) })}
            className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
          />
        </div>
      )}

      {mode === 'between' && (
        <div className="mt-3 flex flex-wrap gap-3">
          <div className="w-[160px]">
            <label className={FIELD_LABEL}>От, дней назад</label>
            <input
              type="number"
              min={0}
              max={365}
              disabled={disabled}
              value={audience.engagement_from_days ?? ''}
              onChange={(event) =>
                onChange({ engagement_from_days: parseDays(event.target.value) })
              }
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
            />
          </div>
          <div className="w-[160px]">
            <label className={FIELD_LABEL}>До, дней назад</label>
            <input
              type="number"
              min={0}
              max={365}
              disabled={disabled}
              value={audience.engagement_to_days ?? ''}
              onChange={(event) =>
                onChange({ engagement_to_days: parseDays(event.target.value) })
              }
              className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function parseDays(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/* -------------------------------------------------------------------------- */
/* Шаблон                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Блоки «Загрузить шаблон» и «Сохранить как шаблон» из ТЗ.
 *
 * Изменено одно: сохранение требует названия. В старом варианте чекбокс
 * «Не сохранять» без имени приводил к списку безымянных шаблонов, в котором
 * через месяц не разобраться.
 */
export function PushTemplateBlock({
  draft,
  disabled,
  canManage,
  onApply,
  onSaved,
}: {
  draft: PushCampaignDraft;
  disabled?: boolean;
  canManage: boolean;
  onApply: (template: PushTemplate) => void;
  onSaved: (message: string) => void;
}) {
  const [templates, setTemplates] = useState<PushTemplate[]>([]);
  const [confirming, setConfirming] = useState<PushTemplate | null>(null);
  const [savingOpen, setSavingOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    marketingPushApi
      .getTemplates()
      .then((response) => setTemplates(asList(response.data)))
      .catch(() => setTemplates([]));
  }, []);

  const options: SelectOption[] = templates.map((template) => ({
    value: template.id,
    label: template.name,
  }));

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      await marketingPushApi.saveTemplate({
        name: name.trim(),
        notification_type: draft.notification_type,
        targets: draft.targets,
        contents: draft.contents,
        engagement_mode: draft.engagement_mode,
        engagement_days: draft.engagement_days,
        engagement_from_days: draft.engagement_from_days,
        engagement_to_days: draft.engagement_to_days,
      });
      const response = await marketingPushApi.getTemplates();
      setTemplates(asList(response.data));
      onSaved(`Шаблон «${name.trim()}» сохранён.`);
      setSavingOpen(false);
      setName('');
    } catch (saveError) {
      setError(pushError(saveError, 'Не удалось сохранить шаблон'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={CARD}>
      <h2 className="text-base font-semibold text-[#2d2c2c] mb-3">Шаблон</h2>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full max-w-[320px]">
          <label className={FIELD_LABEL}>Загрузить шаблон</label>
          <Select
            options={options}
            optionSelected={null}
            placeholder={templates.length ? 'Выберите шаблон' : 'Шаблонов пока нет'}
            isVisible={false}
            isClearable={false}
            customStyles={selectControlStyles}
            onSelected={(option: SelectOption | null) => {
              const template = templates.find((item) => item.id === Number(option?.value));
              if (template) setConfirming(template);
            }}
          />
          <p className="mt-1 text-xs text-gray-500">Заменит содержимое формы целиком.</p>
        </div>

        {canManage && (
          <Button
            isWhite
            disabled={disabled || draft.contents.length === 0}
            onClick={() => setSavingOpen(true)}
          >
            Сохранить как шаблон
          </Button>
        )}
      </div>

      <MyModal
        isOpen={confirming !== null}
        onClose={() => setConfirming(null)}
        title="Применить шаблон?"
        size="sm"
      >
        <p className="text-sm text-[#2d2c2c]">
          Содержимое формы будет заменено содержимым шаблона «{confirming?.name}»:
          приложения, языки, тексты и настройки аудитории.
        </p>
        <p className="mt-2 text-sm text-gray-500">Несохранённые изменения потеряются.</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button isWhite onClick={() => setConfirming(null)}>
            Отмена
          </Button>
          <Button
            onClick={() => {
              if (confirming) onApply(confirming);
              setConfirming(null);
            }}
          >
            Применить
          </Button>
        </div>
      </MyModal>

      <MyModal
        isOpen={savingOpen}
        onClose={() => setSavingOpen(false)}
        title="Сохранить как шаблон"
        size="sm"
      >
        {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
        <label className={FIELD_LABEL}>Название</label>
        <input
          value={name}
          maxLength={200}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          className="h-11 w-full rounded-lg border border-gray-300 px-3 text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          По нему шаблон будут искать через месяц. «Рассылка 1» не подойдёт.
        </p>
        <p className="mt-3 text-sm text-gray-500">
          В шаблон попадут приложения, языки, тексты, картинки, кнопки и настройки
          аудитории. Время отправки и результаты не сохраняются.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button isWhite onClick={() => setSavingOpen(false)}>
            Отмена
          </Button>
          <Button disabled={saving || charLength(name.trim()) < 2} onClick={() => void handleSave()}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </div>
      </MyModal>
    </div>
  );
}
